import { ActionIcon, Button, Divider, Group, Indicator, Popover, ScrollArea, Stack, Text, UnstyledButton } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconBell } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import type { NotificationDto, Paginated } from '@mexp/shared';
import { http } from '@/api/client';
import { fromNow } from '@/lib/format';
import { useAuth } from '@/auth/AuthProvider';

export const notificationKeys = {
  all: ['notifications'] as const,
  unread: ['notifications', 'unread-count'] as const,
  latest: ['notifications', 'latest'] as const,
};

export function NotificationBell() {
  const [opened, { toggle, close }] = useDisclosure(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const unread = useQuery({
    queryKey: notificationKeys.unread,
    queryFn: () => http.get<{ count: number }>('/notifications/unread-count'),
    refetchInterval: 60_000,
  });
  // Only unread ones: a notification leaves the bell once it is opened or marked read (history stays on the page).
  const latest = useQuery({
    queryKey: notificationKeys.latest,
    queryFn: () => http.get<Paginated<NotificationDto>>('/notifications', { limit: 8, unreadOnly: true }),
    enabled: opened,
  });
  const invalidate = () => queryClient.invalidateQueries({ queryKey: notificationKeys.all });
  const markRead = useMutation({ mutationFn: (id: string) => http.post(`/notifications/${id}/read`), onSuccess: invalidate });
  const markAll = useMutation({ mutationFn: () => http.post('/notifications/read-all'), onSuccess: invalidate });

  const count = unread.data?.count ?? 0;
  const open = (n: NotificationDto) => {
    markRead.mutate(n.id);
    close();
    if (n.link) navigate(n.link);
  };

  return (
    <Popover opened={opened} onChange={(o) => !o && close()} width={360} position="bottom-end" shadow="md" withinPortal>
      <Popover.Target>
        <Indicator disabled={count === 0} label={count > 99 ? '99+' : count} size={16} offset={4} color="red">
          <ActionIcon size="lg" onClick={toggle} aria-label={`Notifications${count ? `, ${count} unread` : ''}`}>
            <IconBell size={20} stroke={1.7} />
          </ActionIcon>
        </Indicator>
      </Popover.Target>
      <Popover.Dropdown p={0}>
        <Group justify="space-between" px="md" py="sm">
          <Text fw={600}>Notifications</Text>
          <Button variant="subtle" size="compact-xs" disabled={count === 0} loading={markAll.isPending} onClick={() => markAll.mutate()}>
            Mark all read
          </Button>
        </Group>
        <Divider />
        <ScrollArea.Autosize mah={380}>
          {latest.data?.items.length ? (
            <Stack gap={0}>
              {latest.data.items.map((n) => (
                <UnstyledButton key={n.id} onClick={() => open(n)} px="md" py="sm" style={{ borderBottom: '1px solid var(--mantine-color-default-border)' }}>
                  <Stack gap={2} style={{ minWidth: 0 }}>
                    <Text fz="sm" fw={600} lineClamp={2}>
                      {n.title}
                    </Text>
                    {n.body && (
                      <Text fz="xs" c="dimmed" lineClamp={2}>
                        {n.body}
                      </Text>
                    )}
                    <Text fz="xs" c="dimmed">
                      {fromNow(n.createdAt)}
                      {n.actorName ? ` · ${n.actorName}` : ''}
                    </Text>
                  </Stack>
                </UnstyledButton>
              ))}
            </Stack>
          ) : (
            <Text c="dimmed" fz="sm" ta="center" py="xl">
              {latest.isLoading ? 'Loading…' : "You're all caught up."}
            </Text>
          )}
        </ScrollArea.Autosize>
        <Divider />
        <Button
          variant="subtle"
          fullWidth
          radius={0}
          onClick={() => {
            close();
            navigate(user?.userType === 'exporter' ? '/notifications' : '/internal/notifications');
          }}
        >
          View all notifications
        </Button>
      </Popover.Dropdown>
    </Popover>
  );
}
